import { useState, useEffect } from "react";
import { X, CheckCircle, ChevronDown, PlusCircle, Dumbbell, Users, Shield, Search, Activity, BarChart2 } from "lucide-react";
import { API, SCHEMA_OPTIONS, DOEL_LABELS, DOEL_COLORS } from "./adminConstants";

function SchemaBadge({ schema, onRemove }) {
  const doel = schema.product?.split("-")[0] || "vetverlies";
  const colors = DOEL_COLORS[doel] || DOEL_COLORS.vetverlies;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${colors.bg} ${colors.text} ${colors.border}`}>
      {schema.label}
      {onRemove && (
        <button onClick={onRemove} className="opacity-60 hover:opacity-100 ml-0.5"><X size={10} /></button>
      )}
    </span>
  );
}

function AssignSchemaModal({ user, existingProducts, onClose, onAssigned, api, showToast, token }) {
  const [selected, setSelected] = useState("");
  const [price, setPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const [customSchemas, setCustomSchemas] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(true);
  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API}/api/admin/custom-schemas`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => setCustomSchemas(Array.isArray(d) ? d : (d.schemas || [])))
      .catch(() => {})
      .finally(() => setLoadingCustom(false));
  }, []);

  const available = SCHEMA_OPTIONS.filter(s => !existingProducts.includes(s.product));
  const isCustom = selected.startsWith("custom__");
  const customId = isCustom ? selected.replace("custom__", "") : null;
  const parsedPrice = parseFloat((price || "0").replace(",", ".")) || 0;

  const handleAssign = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      if (isCustom) {
        const res = await fetch(`${API}/api/admin/custom-schemas/${customId}/assign`, {
          method: "POST", headers: authHeaders,
          body: JSON.stringify({ user_id: user.id, price: parsedPrice }),
        });
        if (!res.ok) throw new Error("Toewijzen mislukt");
      } else {
        const opt = SCHEMA_OPTIONS.find(s => s.product === selected);
        if (!opt) return;
        await api.addSchema(user.id, { product: opt.product, label: opt.label, price: parsedPrice });
      }
      showToast(`Schema toegewezen aan ${user.name}`);
      onAssigned();
      onClose();
    } catch (e) {
      showToast(e.message || "Mislukt", "error");
    } finally {
      setLoading(false);
    }
  };

  const hasOptions = available.length > 0 || customSchemas.length > 0;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#111] border border-white/10 rounded-2xl p-7 animate-fadeIn">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-white font-bold text-lg">Schema toewijzen</h2>
            <p className="text-zinc-500 text-xs mt-0.5">aan {user.name}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        {!hasOptions && !loadingCustom ? (
          <div className="text-center py-8">
            <CheckCircle size={32} className="text-[#C9A84C] mx-auto mb-3" />
            <p className="text-white font-semibold text-sm">Alle schema's zijn al toegewezen</p>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-2 block">Kies een schema</label>
              <div className="relative">
                <select value={selected} onChange={e => setSelected(e.target.value)} data-testid="schema-select"
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-3 text-sm text-zinc-300 appearance-none focus:outline-none focus:border-[#C9A84C]/40 pr-10">
                  <option value="">-- Selecteer een schema --</option>
                  {["vetverlies", "spieropbouw", "onderhoud", "powerlift", "hyrox"].map(doel => {
                    const doelSchemas = available.filter(s => s.doel === doel);
                    if (doelSchemas.length === 0) return null;
                    return (
                      <optgroup key={doel} label={`── ${DOEL_LABELS[doel]}`}>
                        {doelSchemas.map(s => <option key={s.product} value={s.product}>{s.label}</option>)}
                      </optgroup>
                    );
                  })}
                  {customSchemas.length > 0 && (
                    <optgroup label="── Custom Schema's (Schema Maker)">
                      {customSchemas.map(s => (
                        <option key={s.id} value={`custom__${s.id}`}>
                          {s.naam || "Naamloos schema"}{s.doel ? ` · ${s.doel}` : ""}{s.niveau ? ` · ${s.niveau}` : ""}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
              <p className="text-zinc-600 text-xs mt-1.5">{available.length} standaard · {customSchemas.length} custom</p>
            </div>
            <div className="mb-6">
              <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-2 block">Betaald bedrag (optioneel)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 text-sm font-semibold">€</span>
                <input type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)}
                  placeholder="0,00" data-testid="schema-price-input"
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg pl-8 pr-4 py-3 text-sm text-zinc-300 focus:outline-none focus:border-[#C9A84C]/40" />
              </div>
              <p className="text-zinc-600 text-xs mt-1.5">Wordt zichtbaar in Bestellingen als betaald bedrag</p>
            </div>
            <div className="flex gap-3">
              <button onClick={onClose} className="btn-course-outline flex-1 py-3 text-sm font-semibold">Annuleren</button>
              <button onClick={handleAssign} disabled={!selected || loading} data-testid="confirm-assign-schema-btn"
                className="btn-gold-course flex-1 py-3 text-sm font-bold uppercase tracking-wide disabled:opacity-40">
                {loading ? "Toewijzen..." : "Toewijzen"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function SchemasView({ users, api, onRefresh, showToast, token }) {
  const [assignModal, setAssignModal] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(null);
  const [search, setSearch] = useState("");
  const [workoutStats, setWorkoutStats] = useState({});
  const [allCustomSchemas, setAllCustomSchemas] = useState([]);

  useEffect(() => {
    api.getWorkoutStats().then(d => setWorkoutStats(d || {})).catch(() => {});
    fetch(`${API}/api/admin/custom-schemas`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setAllCustomSchemas(Array.isArray(d) ? d : (d.schemas || [])))
      .catch(() => {});
  }, []);

  const handleRemove = async (user, product) => {
    setRemoveLoading(`${user.id}-${product}`);
    try {
      await api.removeSchema(user.id, product);
      showToast("Schema verwijderd");
      onRefresh();
    } catch { showToast("Verwijderen mislukt", "error"); }
    setRemoveLoading(null);
  };

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const totalSchemas = users.reduce((acc, u) =>
    acc + (u.purchased_schemas || []).length + (u.custom_schema_ids || []).length, 0);
  const usersWithSchemas = users.filter(u =>
    (u.purchased_schemas || []).length > 0 || (u.custom_schema_ids || []).length > 0).length;

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
        {[
          { icon: <Dumbbell size={18} />, label: "Toegewezen schema's", val: totalSchemas },
          { icon: <Users size={18} />, label: "Cursisten met schema's", val: usersWithSchemas },
          { icon: <Shield size={18} />, label: "Beschikbare schema's", val: 36 },
        ].map(({ icon, label, val }) => (
          <div key={label} className="course-stat-card">
            <div className="text-[#C9A84C] mb-2">{icon}</div>
            <div className="text-2xl font-bold text-white">{val}</div>
            <div className="text-zinc-500 text-xs mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Zoeken op naam of e-mail..." className="course-input pl-9 max-w-sm text-sm" />
      </div>

      <div className="space-y-3">
        {filtered.map(u => {
          const schemas = u.purchased_schemas || [];
          return (
            <div key={u.id} className="course-card p-5" data-testid={`schema-row-${u.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
                    <span className="text-[#C9A84C] text-xs font-bold">
                      {u.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm">{u.name}</p>
                    <p className="text-zinc-500 text-xs truncate">{u.email}</p>
                  </div>
                </div>
                <button onClick={() => setAssignModal({ user: u })} data-testid={`assign-schema-btn-${u.id}`}
                  className="btn-course-outline text-xs px-3 py-1.5 flex items-center gap-1.5 shrink-0">
                  <PlusCircle size={12} /> Schema toewijzen
                </button>
              </div>
              <div className="mt-4">
                {schemas.length === 0 ? (
                  <p className="text-zinc-600 text-xs italic">Nog geen schema's toegewezen</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {schemas.map(s => {
                      const userStats = workoutStats[u.id] || {};
                      const count = userStats[s.product] || 0;
                      return (
                        <div key={s.product} className="flex items-center gap-1.5">
                          <SchemaBadge schema={s}
                            onRemove={removeLoading === `${u.id}-${s.product}` ? null : () => handleRemove(u, s.product)} />
                          {count > 0 && (
                            <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 rounded-full px-2 py-0.5" title={`${count}x dit schema getraind`}>
                              <Activity size={9} />{count}x
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Custom schema's (schema maker) */}
              {(u.custom_schema_ids || []).length > 0 && (() => {
                const resolvedCustom = (u.custom_schema_ids || [])
                  .map(id => allCustomSchemas.find(s => s.id === id))
                  .filter(Boolean);
                if (resolvedCustom.length === 0) return null;
                return (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {resolvedCustom.map(s => (
                      <span key={s.id}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border bg-purple-900/30 text-purple-300 border-purple-500/30"
                        title="Custom schema (Schema Maker)">
                        <Dumbbell size={9} />
                        {s.naam || "Custom schema"}
                        {s.doel ? ` · ${s.doel}` : ""}
                      </span>
                    ))}
                  </div>
                );
              })()}
              {(schemas.length > 0 || (u.custom_schema_ids || []).length > 0) && (
                <div className="flex items-center gap-3 mt-2">
                  <p className="text-zinc-700 text-xs">
                    {schemas.length + (u.custom_schema_ids || []).length} schema{(schemas.length + (u.custom_schema_ids || []).length) !== 1 ? "'s" : ""} toegewezen
                    {(u.custom_schema_ids || []).length > 0 && (
                      <span className="ml-1 text-purple-400">({(u.custom_schema_ids || []).length} custom)</span>
                    )}
                  </p>
                  {workoutStats[u.id]?.__total__ > 0 && (
                    <span className="flex items-center gap-1 text-xs text-zinc-500">
                      <BarChart2 size={10} className="text-[#C9A84C]" />
                      <span className="text-[#C9A84C] font-semibold">{workoutStats[u.id].__total__}</span> trainingen gelogd
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {assignModal && (
        <AssignSchemaModal
          user={assignModal.user}
          existingProducts={(assignModal.user.purchased_schemas || []).map(s => s.product)}
          onClose={() => setAssignModal(null)}
          onAssigned={onRefresh}
          api={api}
          showToast={showToast}
          token={token}
        />
      )}
    </div>
  );
}
